import { create } from 'zustand';
import type {
  StakeScenarioDetail,
  StakeScenarioMeta,
  StakeScenarioRow,
} from '@sr-test-wizard/shared';
import { stakeEngineApi, type ScenarioQuery } from '../lib/stakeEngineApi';

/**
 * State for the Stake Engine "Scenarios / Debug" tab. Kept separate from the
 * verification store so the heavy list/detail data does not re-render the
 * gates. The selected mode's meta (facets + shapes) drives the filter bar; rows
 * are server-filtered + paged; detail is fetched lazily per record.
 */

export type ScenarioFilterState = Pick<
  ScenarioQuery,
  'winType' | 'features' | 'multMin' | 'multMax' | 'attrs' | 'criteria' | 'eventType' | 'id' | 'explained'
>;

type Sort = NonNullable<ScenarioQuery['sort']>;

interface ScenariosState {
  runId: string | null;
  mode: string | null;
  meta: StakeScenarioMeta | null;
  rows: StakeScenarioRow[];
  total: number;
  page: number;
  pageSize: number;
  sort: Sort;
  filters: ScenarioFilterState;

  metaLoading: boolean;
  rowsLoading: boolean;
  building: boolean;
  explainingShapes: boolean;
  shapesGenerated: boolean;
  error: string | null;

  detail: StakeScenarioDetail | null;
  detailLoading: boolean;
  detailExplaining: boolean;

  // Bulk per-round explanation.
  selectedIds: Set<number>;
  explainedIds: Set<number>;
  bulkExplaining: boolean;
  selectingAll: boolean;

  init: (runId: string) => void;
  selectMode: (mode: string) => Promise<void>;
  build: () => Promise<void>;
  setFilters: (patch: Partial<ScenarioFilterState>) => void;
  clearFilters: () => void;
  setSort: (sort: Sort) => void;
  setPage: (page: number) => void;
  query: () => Promise<void>;
  openRecord: (id: number) => Promise<void>;
  closeRecord: () => void;
  explainShapes: () => Promise<void>;
  explainRecord: (id: number) => Promise<void>;
  toggleSelect: (id: number) => void;
  toggleSelectPage: (select: boolean) => void;
  selectAllFiltered: () => Promise<void>;
  clearSelection: () => void;
  explainSelected: () => Promise<void>;
  stopExplain: () => Promise<void>;
  reset: () => void;
}

const EMPTY_FILTERS: ScenarioFilterState = {};

export const useStakeScenariosStore = create<ScenariosState>((set, get) => ({
  runId: null,
  mode: null,
  meta: null,
  rows: [],
  total: 0,
  page: 0,
  pageSize: 50,
  sort: 'idAsc',
  filters: EMPTY_FILTERS,
  metaLoading: false,
  rowsLoading: false,
  building: false,
  explainingShapes: false,
  shapesGenerated: false,
  error: null,
  detail: null,
  detailLoading: false,
  detailExplaining: false,
  selectedIds: new Set<number>(),
  explainedIds: new Set<number>(),
  bulkExplaining: false,
  selectingAll: false,

  init(runId) {
    if (get().runId === runId) return;
    set({
      runId,
      mode: null,
      meta: null,
      rows: [],
      total: 0,
      page: 0,
      sort: 'idAsc',
      filters: EMPTY_FILTERS,
      shapesGenerated: false,
      detail: null,
      error: null,
      selectedIds: new Set<number>(),
      explainedIds: new Set<number>(),
    });
  },

  async selectMode(mode) {
    const { runId } = get();
    if (!runId) return;
    set({
      mode,
      meta: null,
      rows: [],
      total: 0,
      page: 0,
      filters: EMPTY_FILTERS,
      shapesGenerated: false,
      detail: null,
      metaLoading: true,
      error: null,
      selectedIds: new Set<number>(),
      explainedIds: new Set<number>(),
    });
    try {
      const { meta } = await stakeEngineApi.scenariosMeta(runId, mode);
      set({ meta, metaLoading: false });
      if (meta) {
        await get().query();
        try {
          const { ids } = await stakeEngineApi.scenariosExplained(runId, mode);
          set({ explainedIds: new Set(ids) });
        } catch {
          /* explained set is best-effort */
        }
      }
    } catch (err) {
      set({ metaLoading: false, error: (err as Error).message });
    }
  },

  async build() {
    const { runId, mode } = get();
    if (!runId || !mode) return;
    set({ building: true, error: null });
    try {
      const { meta } = await stakeEngineApi.scenariosBuild(runId, mode);
      set({ meta, building: false, page: 0 });
      await get().query();
    } catch (err) {
      set({ building: false, error: (err as Error).message });
    }
  },

  setFilters(patch) {
    // Changing the filter changes the population, so drop any stale selection
    // (otherwise "N selected" can hugely mismatch what's on screen).
    set((s) => ({ filters: { ...s.filters, ...patch }, page: 0, selectedIds: new Set<number>() }));
    void get().query();
  },

  clearFilters() {
    set({ filters: EMPTY_FILTERS, page: 0, selectedIds: new Set<number>() });
    void get().query();
  },

  setSort(sort) {
    set({ sort, page: 0 });
    void get().query();
  },

  setPage(page) {
    set({ page });
    void get().query();
  },

  async query() {
    const { runId, mode, filters, sort, page, pageSize } = get();
    if (!runId || !mode) return;
    set({ rowsLoading: true });
    try {
      const res = await stakeEngineApi.scenariosRows(runId, mode, { ...filters, sort, page, pageSize });
      set({ rows: res.rows, total: res.total, rowsLoading: false });
    } catch (err) {
      set({ rowsLoading: false, error: (err as Error).message });
    }
  },

  async openRecord(id) {
    const { runId, mode } = get();
    if (!runId || !mode) return;
    set({ detailLoading: true, detail: null });
    try {
      const { detail } = await stakeEngineApi.scenarioRecord(runId, mode, id);
      set({ detail, detailLoading: false });
    } catch (err) {
      set({ detailLoading: false, error: (err as Error).message });
    }
  },

  closeRecord() {
    set({ detail: null });
  },

  async explainShapes() {
    const { runId, mode } = get();
    if (!runId || !mode) return;
    set({ explainingShapes: true, error: null });
    try {
      const { shapes } = await stakeEngineApi.scenariosExplainShapes(runId, mode);
      set({ explainingShapes: false, shapesGenerated: shapes.items.length > 0 });
      // If a record is open, refresh it so its shape explanation appears.
      const open = get().detail;
      if (open) await get().openRecord(open.summary.id);
    } catch (err) {
      set({ explainingShapes: false, error: (err as Error).message });
    }
  },

  async explainRecord(id) {
    const { runId, mode, detail } = get();
    if (!runId || !mode) return;
    set({ detailExplaining: true });
    try {
      const { explanation } = await stakeEngineApi.scenarioExplainOne(runId, mode, id);
      set((s) => ({
        detailExplaining: false,
        detail: detail && detail.summary.id === id ? { ...detail, explanation } : detail,
        explainedIds: new Set(s.explainedIds).add(id),
      }));
    } catch (err) {
      set({ detailExplaining: false, error: (err as Error).message });
    }
  },

  toggleSelect(id) {
    set((s) => {
      const next = new Set(s.selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedIds: next };
    });
  },

  toggleSelectPage(select) {
    set((s) => {
      const next = new Set(s.selectedIds);
      for (const r of s.rows) {
        if (select) next.add(r.id);
        else next.delete(r.id);
      }
      return { selectedIds: next };
    });
  },

  async selectAllFiltered() {
    const { runId, mode, filters, sort } = get();
    if (!runId || !mode) return;
    set({ selectingAll: true });
    try {
      const { ids } = await stakeEngineApi.scenariosIds(runId, mode, { ...filters, sort });
      set({ selectedIds: new Set(ids), selectingAll: false });
    } catch (err) {
      set({ selectingAll: false, error: (err as Error).message });
    }
  },

  clearSelection() {
    set({ selectedIds: new Set<number>() });
  },

  async explainSelected() {
    const { runId, mode, selectedIds } = get();
    if (!runId || !mode || selectedIds.size === 0) return;
    set({ bulkExplaining: true, error: null });
    // The server persists each batch as it finishes, so poll the explained set
    // while the run is in flight to surface the ✓ marks in near real-time.
    const poll = setInterval(() => {
      void stakeEngineApi
        .scenariosExplained(runId, mode)
        .then(({ ids }) => set({ explainedIds: new Set(ids) }))
        .catch(() => undefined);
    }, 4000);
    try {
      await stakeEngineApi.scenariosExplainRounds(runId, mode, [...selectedIds]);
      // Final refresh of the explained set + any open record.
      const { ids } = await stakeEngineApi.scenariosExplained(runId, mode);
      set({ bulkExplaining: false, explainedIds: new Set(ids), selectedIds: new Set<number>() });
      const open = get().detail;
      if (open) await get().openRecord(open.summary.id);
    } catch (err) {
      set({ bulkExplaining: false, error: (err as Error).message });
    } finally {
      clearInterval(poll);
    }
  },

  async stopExplain() {
    const { runId, mode } = get();
    if (!runId || !mode) return;
    try {
      await stakeEngineApi.scenariosExplainRoundsStop(runId, mode);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  reset() {
    set({
      runId: null,
      mode: null,
      meta: null,
      rows: [],
      total: 0,
      detail: null,
      shapesGenerated: false,
      selectedIds: new Set<number>(),
      explainedIds: new Set<number>(),
    });
  },
}));
